import { IconNames } from '@icon/assets';
import { OrderSide } from '@shared-types';
import { OrderFormInputData } from './order-form.model';
import { OrderResponseDto, OrderSideEnum, OrderStateEnum } from './order-response.model';

/**
 * A single execution (fill) of an order. An order may be executed in several
 * parts at different prices, so each trade carries its own price.
 */
export interface OrderTrade {
	id: string;
	quantity: number;
	/** The price this part was actually executed at — not the order price. */
	price: number;
	executedAt: string;
}

/**
 * Presentation model consumed by the order cards/detail sheet: the API response as-is,
 * plus the handful of display fields the API doesn't return (icon, per-fill breakdown). `mapOrderResponseToOrder` (order-response.mapper.ts) fills those in from the
 * `/orders` payload alone — the orders list makes no market-data call of its own.
 *
 * `symbolName` is inherited from the DTO untouched — null when the API has no name for the
 * symbol, which the UI renders as a blank title rather than substituting the code or isin.
 */
export interface Order extends OrderResponseDto {
	/** Commodity icon, picked by `symbolIcon` from the DTO's `symbol` code. */
	symbolIcon: IconNames;
	/** نام کوتاه نماد, straight from the DTO; empty when the API omits it. */
	symbolShortName: string;
	/** Per-fill breakdown, fetched from `/trades/{orderId}` only for the order being opened. */
	trades: OrderTrade[];
	/**
	 * True once the order has filled. Narrower than "no longer open": an order that was
	 * cancelled, rejected or expired is neither open nor executed.
	 */
	isExecuted: boolean;
	/** True when the order's last action was a modification. */
	isEdited: boolean;
	/**
	 * The symbol's per-side commission rates as **fractions** (`0.0024` for 0.24%), taken
	 * from the symbol snapshot when the detail sheet opens. `/orders` carries no commission of its
	 * own, so both stay 0 until then — and a value computed at 0 is simply the raw
	 * price x quantity, never a figure inflated by a percentage read as a fraction.
	 */
	buyCommissionRate: number;
	sellCommissionRate: number;
}

/** States in which the order can be edited or cancelled. */
const OPEN_ORDER_STATES = new Set<OrderStateEnum>([
	OrderStateEnum.OnBoard,
	OrderStateEnum.PartiallyExcution,
	OrderStateEnum.OnModifyBoard,
	OrderStateEnum.OnModifyError,
	OrderStateEnum.OnCancelError
]);

/** States an order ends in when it ran out of time — shown on the page, but no longer actionable. */
const EXPIRED_ORDER_STATES = new Set<OrderStateEnum>([
	OrderStateEnum.Expired,
	OrderStateEnum.PartiallyExcutionAndExpired
]);

/**
 * States the orders page shows. `/orders` answers with the account's whole history, so
 * everything outside this set — cancelled, rejected, deleted — belongs to an earlier session
 * and is filtered out before the list ever sees it. The expired states stay: an order that
 * ran out today is still today's, and the card marks it with the expired badge.
 */
const TODAY_ORDER_STATES = new Set<OrderStateEnum>([
	OrderStateEnum.Done,
	OrderStateEnum.OnBoard,
	OrderStateEnum.OnSending,
	OrderStateEnum.PartiallyExcution,
	OrderStateEnum.OnCanceling,
	OrderStateEnum.OnCancelError,
	OrderStateEnum.OnModify,
	OrderStateEnum.OnModifyBoard,
	OrderStateEnum.OnModifyError,
	OrderStateEnum.OrderExecuted,
	OrderStateEnum.PendingConfirm,
	OrderStateEnum.Confirmed,
	OrderStateEnum.Expired,
	OrderStateEnum.PartiallyExcutionAndExpired,
	OrderStateEnum.CancelPendingOrderByUser
]);

export const ORDER_STATE_LABELS: Record<OrderStateEnum, string> = {
	[OrderStateEnum.None]: 'نامشخص',
	[OrderStateEnum.Modify]: 'ویرایش',
	[OrderStateEnum.Error]: 'خطا',
	[OrderStateEnum.Cancel]: 'حذف شده',
	[OrderStateEnum.Delete]: 'حذف شده',
	[OrderStateEnum.Done]: 'انجام‌شده',
	[OrderStateEnum.OnBoard]: 'ثبت در سیستم معاملات',
	[OrderStateEnum.OnSending]: 'در حال ارسال',
	[OrderStateEnum.PartiallyExcution]: 'بخشی انجام‌شده',
	[OrderStateEnum.OnCanceling]: 'در حال حذف',
	[OrderStateEnum.OnCancelError]: 'خطا در حذف',
	[OrderStateEnum.OnModifyError]: 'خطا در ویرایش',
	[OrderStateEnum.DeleteByBroker]: 'حذف شده توسط کارگزار',
	[OrderStateEnum.Expired]: 'منقضی شده',
	[OrderStateEnum.PartiallyExcutionAndExpired]: 'بخشی انجام‌ و مابقی منقضی شده',
	[OrderStateEnum.CancelByBrokerForUnBlock]: 'حذف شده توسط کارگزار',
	[OrderStateEnum.OnModify]: 'در حال ویرایش',
	[OrderStateEnum.DeleteByNSC]: 'حذف شده توسط بورس کالا',
	[OrderStateEnum.CancelByBrokerForOrderOnAir]: 'حذف شده توسط کارگزار',
	[OrderStateEnum.OnModifyBoard]: 'ثبت در سیستم معاملات (ویرایش شده)',
	[OrderStateEnum.OrderExecuted]: 'انجام‌شده',
	[OrderStateEnum.PendingConfirm]: 'در انتظار تایید',
	[OrderStateEnum.Confirmed]: 'تایید شده',
	[OrderStateEnum.PendingCancelOrder]: 'در انتظار حذف',
	[OrderStateEnum.CancelOrderConfirmed]: 'حذف تایید شده',
	[OrderStateEnum.Reject]: 'رد شده',
	[OrderStateEnum.CancelPendingOrderByUser]: 'در انتظار حذف توسط کاربر',
	[OrderStateEnum.CancelCancelPendingOrderByUser]: 'انصراف از حذف توسط کاربر',
	[OrderStateEnum.InOmsQueue]: 'در صف پردازش'
};

export function getOrderStatusLabel(order: Order): string {
	return ORDER_STATE_LABELS[order.state];
}

/** An open order is active and not yet fully executed, so it can be edited or cancelled. */
export function isOpenOrder(order: Order): boolean {
	return OPEN_ORDER_STATES.has(order.state);
}

export function isExpiredOrder(order: Order): boolean {
	return EXPIRED_ORDER_STATES.has(order.state);
}

/**
 * Whether the order filled. Deliberately not the complement of `isOpenOrder`: an order that
 * ended without filling is neither, which is what keeps it out of the «انجام‌شده» filter.
 *
 * Both states the exchange labels 'انجام‌شده' count, so the flag can't disagree with the
 * status the card already shows next to it.
 */
export function isExecutedOrder(state: OrderStateEnum): boolean {
	return state === OrderStateEnum.Done || state === OrderStateEnum.OrderExecuted;
}

/** Whether the orders page shows an order in this state — see `TODAY_ORDER_STATES`. */
export function isTodayOrderState(state: OrderStateEnum): boolean {
	return TODAY_ORDER_STATES.has(state);
}

/**
 * Where an order sits in the list: the open ones first, then those still in flight, and the
 * filled ones last — so what the user can still act on is never buried under trades that are
 * already done.
 *
 * Three ranks rather than two, because "not open" covers orders on their way to the exchange
 * (در حال ارسال, در انتظار تایید, در حال حذف) as well as executed ones; only the latter belong
 * at the bottom.
 */
function orderListRank(order: Order): number {
	if (isOpenOrder(order)) {
		return 0;
	}
	return order.isExecuted ? 2 : 1;
}

/**
 * Orders the list by `orderListRank`, on a copy. The sort is stable, so within each group the
 * API's own ordering is left exactly as it came.
 */
export function sortOrdersByListPriority(orders: readonly Order[]): Order[] {
	return [...orders].sort((first, second) => orderListRank(first) - orderListRank(second));
}

/**
 * The orders API speaks `OrderSideEnum` ('Buy'/'Sell'), the order form speaks the
 * shared numeric `OrderSide`. Bridges the two when handing an order off to the form.
 */
export function toOrderFormSide(side: OrderSideEnum): OrderSide {
	return side === OrderSideEnum.Sell ? OrderSide.Sell : OrderSide.Buy;
}

/**
 * Turns an existing order into order-form input. Editing carries the order id and only
 * the unfilled part; copying seeds a brand new order from the original's full volume.
 */
export function toOrderFormInput(
	order: Order,
	{ isEdit, isCopy }: { isEdit?: boolean; isCopy?: boolean }
): OrderFormInputData {
	return {
		isin: order.symbolIsin,
		side: toOrderFormSide(order.side),
		price: order.price,
		quantity: isEdit ? getRemainingQuantity(order) : order.volume,
		isEdit,
		isCopy,
		...(isEdit ? { orderId: order.orderId } : {})
	};
}

/** The API reports the unfilled part directly, so it's taken as-is rather than recomputed. */
export function getRemainingQuantity(order: Order): number {
	return Math.max(order.remainingVolume, 0);
}

/**
 * A trade prepared for display: its own share of the order, how much of the order
 * was already filled before it, and its rial value.
 */
export interface OrderTradeRow extends OrderTrade {
	/** Width of this part's segment, as a percentage of the whole order. */
	percent: number;
	/** Where this part's segment starts, i.e. the cumulative fill before it. */
	offsetPercent: number;
	value: number;
}

/**
 * Lays the executions out along a single 0-100% track: each part starts where the
 * previous one ended, so together they tile the order's total progress.
 */
export function getTradeRows(order: Order): OrderTradeRow[] {
	const quantity = order.volume;
	let filledBefore = 0;

	return order.trades.map(trade => {
		const row: OrderTradeRow = {
			...trade,
			percent: getTradePercent(order, trade),
			offsetPercent: quantity === 0 ? 0 : Math.round((filledBefore / quantity) * 100),
			value: getTradeValue(order, trade)
		};
		filledBefore += trade.quantity;
		return row;
	});
}

/** Share of the whole order this single execution accounts for. */
export function getTradePercent(order: Order, trade: OrderTrade): number {
	const quantity = order.volume;
	if (quantity === 0) {
		return 0;
	}
	return Math.round((trade.quantity / quantity) * 100);
}

/**
 * Rial value of a single execution, commission included — what the fill actually cost or
 * returned, not the bare price x quantity.
 *
 * Both figures are the execution's own: the quantity that part filled, at the price it
 * traded at, which is neither the order's volume nor the price it was placed at. A buy
 * adds the commission to what is paid, a sell deducts it from what is received:
 *
 * - buy:  `price * quantity * (1 + buyCommissionRate)`
 * - sell: `price * quantity * (1 - sellCommissionRate)`
 */
export function getTradeValue(order: Order, trade: OrderTrade): number {
	const gross = trade.quantity * trade.price;
	if (!gross) {
		return 0;
	}

	const value =
		order.side === OrderSideEnum.Buy
			? gross * (1 + order.buyCommissionRate)
			: gross * (1 - order.sellCommissionRate);

	return Math.round(value);
}
